import React, { useState, useEffect } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Text,
} from 'react-native';
import { theme } from '../../theme/theme';
import { SearchIcon, FilterIcon, CloseIcon } from '../icons';

interface SearchBarProps {
  value: string;
  onChangeText: (text: string) => void;
  onSearch: (query: string) => void;
  onFilterPress: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  showFilter?: boolean;
  hasActiveFilters?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChangeText,
  onSearch,
  onFilterPress,
  onFocus,
  onBlur,
  placeholder = '搜索艺术作品、艺术家...',
  showFilter = true,
  hasActiveFilters = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = () => {
    onSearch(value.trim());
  };

  const handleClear = () => {
    onChangeText('');
    onSearch('');
  };

  return (
    <View style={styles.container}>
      <View style={[
        styles.searchContainer,
        isFocused && styles.searchContainerFocused
      ]}>
        <SearchIcon 
          size={20} 
          color={isFocused ? theme.colors.primary : theme.colors.textSecondary} 
        />
        
        <TextInput
          style={styles.input}
          value={value}
          onChangeText={onChangeText}
          onSubmitEditing={handleSubmit}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          placeholderTextColor={theme.colors.textSecondary}
          returnKeyType="search"
        />

        {value.length > 0 && (
          <TouchableOpacity onPress={handleClear} style={styles.clearButton}>
            <CloseIcon size={16} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        )}
      </View>

      {showFilter && (
        <TouchableOpacity 
          onPress={onFilterPress} 
          style={[
            styles.filterButton,
            hasActiveFilters && styles.filterButtonActive
          ]}
        >
          <FilterIcon 
            size={20} 
            color={hasActiveFilters ? 'white' : theme.colors.text} 
          />
          {hasActiveFilters && (
            <View style={styles.filterIndicator} />
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.surface,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.bg,
    borderRadius: theme.borderRadius.lg,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  searchContainerFocused: {
    borderColor: theme.colors.primary,
    borderWidth: 2,
  },
  input: {
    flex: 1,
    fontSize: theme.fontSize.base,
    color: theme.colors.text,
    marginLeft: theme.spacing.sm,
    marginRight: theme.spacing.sm,
    paddingVertical: 0, // Remove default padding
  },
  clearButton: {
    padding: theme.spacing.xs,
  },
  filterButton: {
    marginLeft: theme.spacing.md,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.bg,
    borderWidth: 1,
    borderColor: theme.colors.border,
    position: 'relative',
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  filterIndicator: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#ef4444',
  },
});
